import { Component, Show } from "solid-js";

export type LogoSize = "xs" | "sm" | "md" | "lg" | "xl";
export type LogoVariant = "default" | "compact" | "full";

export interface LogoProps {
  /**
   * Size of the logo
   * @default "md"
   */
  size?: LogoSize;
  /**
   * Variant of the logo display
   * - default: logo image/SVG only
   * - compact: logo without text
   * - full: logo with text beside it
   * @default "default"
   */
  variant?: LogoVariant;
  /**
   * Image source for the logo (if using an image instead of SVG)
   * If provided, uses img element instead of SVG
   */
  src?: string;
  /**
   * Alt text for the logo
   * @default "AirBrx Logo"
   */
  alt?: string;
  /**
   * Additional CSS class
   */
  class?: string;
  /**
   * Whether to show the text label
   * When variant is "full", this is always true
   * @default false
   */
  showText?: boolean;
  /**
   * Custom text to display
   * @default "AirBrx"
   */
  text?: string;
  /**
   * Click handler
   */
  onClick?: () => void;
}

const sizeMap: Record<LogoSize, { width: number; height: number; fontSize: string }> = {
  xs: { width: 16, height: 16, fontSize: "0.75rem" },
  sm: { width: 24, height: 24, fontSize: "0.875rem" },
  md: { width: 32, height: 32, fontSize: "1rem" },
  lg: { width: 48, height: 48, fontSize: "1.25rem" },
  xl: { width: 64, height: 64, fontSize: "1.5rem" },
};

/**
 * AirBrx Logo Component
 *
 * A flexible logo component that supports:
 * - Image-based logos (using src prop)
 * - SVG-based logos (default fallback)
 * - Optional text label
 * - Multiple sizes (xs, sm, md, lg, xl)
 * - Multiple variants (default, compact, full)
 *
 * @example
 * ```tsx
 * // Basic usage with SVG fallback
 * <Logo size="md" />
 *
 * // With image source
 * <Logo src="/logo.png" size="lg" />
 *
 * // With text label
 * <Logo size="md" showText />
 *
 * // Full variant with text
 * <Logo variant="full" size="lg" />
 * ```
 */
export const Logo: Component<LogoProps> = (props) => {
  const size = () => props.size ?? "md";
  const variant = () => props.variant ?? "default";
  const showText = () => props.showText || variant() === "full";
  const text = () => props.text ?? "AirBrx";
  const alt = () => props.alt ?? "AirBrx Logo";
  const dimensions = () => sizeMap[size()];

  const containerStyles = (): Record<string, string> => ({
    display: "flex",
    "align-items": "center",
    gap: "0.5rem",
    cursor: props.onClick ? "pointer" : "default",
  });

  const textStyles = (): Record<string, string> => ({
    "font-weight": "600",
    "font-size": dimensions().fontSize,
    color: "inherit",
  });

  return (
    <div
      class={`logo ${props.class ?? ""}`}
      style={containerStyles()}
      onClick={props.onClick}
      role={props.onClick ? "button" : undefined}
      tabIndex={props.onClick ? 0 : undefined}
    >
      <Show
        when={props.src}
        fallback={
          <DefaultLogoSvg
            width={dimensions().width}
            height={dimensions().height}
            alt={alt()}
          />
        }
      >
        <img
          src={props.src}
          alt={alt()}
          style={{
            height: `${dimensions().height}px`,
            width: "auto",
            display: "block",
          }}
        />
      </Show>
      <Show when={showText()}>
        <span style={textStyles()}>{text()}</span>
      </Show>
    </div>
  );
};

/**
 * Default SVG logo fallback
 */
const DefaultLogoSvg: Component<{ width: number; height: number; alt: string }> = (props) => {
  return (
    <svg
      width={props.width}
      height={props.height}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label={props.alt}
    >
      <rect width="32" height="32" rx="6" fill="#fd6c1d" />
      <path
        d="M8 22L16 10L24 22H8Z"
        fill="white"
        stroke="white"
        stroke-width="2"
        stroke-linejoin="round"
      />
      <circle cx="16" cy="18" r="2" fill="#fd6c1d" />
    </svg>
  );
};

/**
 * LogoIcon - Standalone SVG logo without container
 * Useful when you need just the icon without any wrapper styling
 */
export const LogoIcon: Component<{ size?: LogoSize; class?: string }> = (props) => {
  const size = () => props.size ?? "md";
  const dimensions = () => sizeMap[size()];

  return (
    <svg
      width={dimensions().width}
      height={dimensions().height}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-label="AirBrx Logo"
    >
      <rect width="32" height="32" rx="6" fill="#fd6c1d" />
      <path
        d="M8 22L16 10L24 22H8Z"
        fill="white"
        stroke="white"
        stroke-width="2"
        stroke-linejoin="round"
      />
      <circle cx="16" cy="18" r="2" fill="#fd6c1d" />
    </svg>
  );
};
